import { useAtom, useAtomValue, useSetAtom } from "jotai";

import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { type MenuOption, useMenu } from "src/components/Menu";
import type { MenuTriggerEvent } from "src/components/Menu/store/types";
import {
  planogramSelectedShelfAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { useTranslation } from "src/i18n";
import { useToasts } from "src/components/Toasts";
import { useEditShelfModal } from "src/modals/EditShelf/store/hooks";
import { KeyCode } from "src/utils/keys";
import { usePogAtomActions } from "../../store/actions";
import type { PogAtom } from "../../store/types";
import { getBoundaryShelvesPerBay, resolvePlanogramShelves } from "../../store/utils";
import { unrangedShelfIdAtom } from "../Panel/panels/UnrangedItems/store/atoms";

export const useShelfContextMenu = (
  pogAtom: PogAtom,
  setIsModified?: (modified: boolean) => void,
) => {
  const { openEditShelfModal } = useEditShelfModal();
  const { t } = useTranslation("common");
  const { toast } = useToasts();
  const actions = usePogAtomActions(pogAtom);
  const { openMenu } = useMenu();
  const pog = useAtomValue(pogAtom);
  const [selectedShelf, setSelectedShelf] = useAtom(planogramSelectedShelfAtom);
  const [selectedShelfIds, setSelectedShelfIds] = useAtom(planogramSelectedShelvesAtom);
  const setUnrangedShelfId = useSetAtom(unrangedShelfIdAtom);

  return (shelf: PlanogramShelf, event: MenuTriggerEvent) => {
    const selectedShelves = resolvePlanogramShelves(pog, selectedShelfIds);
    const isPartOfSelection = selectedShelfIds.includes(shelf.uniqueId);
    const targetShelves =
      isPartOfSelection && selectedShelves.length > 1 ? selectedShelves : [shelf];
    const isMultiple = targetShelves.length > 1;
    if (!isPartOfSelection) {
      setSelectedShelfIds([shelf.uniqueId]);
      setSelectedShelf(shelf);
    }

    const canMoveUp = getBoundaryShelvesPerBay(targetShelves, "top").every(actions.canBeMovedUp);
    const canMoveDown = getBoundaryShelvesPerBay(targetShelves, "bottom").every(
      actions.canBeMovedDown,
    );
    const ids = targetShelves.map((target) => target.uniqueId);
    const options: MenuOption[] = [
      canMoveUp
        ? {
            label: t("planogramCanvas.shelfMenu.moveUp"),
            keybind: { keyCode: KeyCode.UP },
            onClick: () => actions.moveShelves(ids, 1),
          }
        : null,
      canMoveDown
        ? {
            label: t("planogramCanvas.shelfMenu.moveDown"),
            keybind: { keyCode: KeyCode.DOWN },
            onClick: () => actions.moveShelves(ids, -1),
          }
        : null,
      {
        label: t("planogramCanvas.shelfMenu.duplicate"),
        keybind: { keyCode: KeyCode.D },
        onClick: () => actions.duplicateShelves(targetShelves),
      },
      {
        label: t("planogramCanvas.shelfMenu.sortCdt1"),
        onClick: () => actions.sortShelvesItemsBy(targetShelves, "cdt1Priority"),
      },
      {
        label: t("planogramCanvas.shelfMenu.sortCdt2"),
        onClick: () => actions.sortShelvesItemsBy(targetShelves, "cdt2Priority"),
      },
      {
        label: t("planogramCanvas.shelfMenu.sortCdt3"),
        keybind: { keyCode: KeyCode.S },
        onClick: () => actions.sortShelvesItemsBy(targetShelves, "cdt3Priority"),
      },
      {
        label: t("planogramCanvas.shelfMenu.flipHorizontally"),
        keybind: { keyCode: KeyCode.F },
        onClick: () => actions.flipShelvesHorizontally(targetShelves),
      },
      {
        label: t("editShelfModal.menuLabel"),
        onClick: () => {
          if (isMultiple) {
            toast({
              title: t("editShelfModal.multipleShelvesEditUnavailable"),
              type: "WARNING",
            });
            return;
          }
          openEditShelfModal(targetShelves, pogAtom, setIsModified);
        },
      },
      targetShelves.every((target) => target.mergedLeft)
        ? {
            label: t("planogramCanvas.shelfMenu.unmergeLeft"),
            keybind: { keyCode: KeyCode.LEFT },
            onClick: () => actions.unmergeShelvesLeft(targetShelves),
          }
        : null,
      targetShelves.every((target) => target.canBeMergedLeft())
        ? {
            label: t("planogramCanvas.shelfMenu.mergeLeft"),
            keybind: { keyCode: KeyCode.LEFT },
            onClick: () => actions.mergeShelvesLeft(targetShelves),
          }
        : null,
      targetShelves.every((target) => target.mergedRight)
        ? {
            label: t("planogramCanvas.shelfMenu.unmergeRight"),
            keybind: { keyCode: KeyCode.RIGHT },
            onClick: () => actions.unmergeShelvesRight(targetShelves),
          }
        : null,
      targetShelves.every((target) => target.canBeMergedRight())
        ? {
            label: t("planogramCanvas.shelfMenu.mergeRight"),
            keybind: { keyCode: KeyCode.RIGHT },
            onClick: () => actions.mergeShelvesRight(targetShelves),
          }
        : null,
      !isMultiple
        ? {
            label: t("planogramCanvas.shelfMenu.viewUnranged"),
            keybind: { keyCode: KeyCode.U },
            onClick: () => setUnrangedShelfId(shelf.uniqueId),
          }
        : null,
      {
        label: t("planogramCanvas.shelfMenu.delete"),
        keybind: { keyCode: KeyCode.DELETE },
        onClick: () => {
          if (
            selectedShelf &&
            targetShelves.some((target) => target.uniqueId === selectedShelf.uniqueId)
          ) {
            setSelectedShelf(null);
          }
          setSelectedShelfIds((current) => current.filter((id) => !ids.includes(id)));
          actions.deleteShelves(targetShelves);
        },
      },
    ].filter((option) => option !== null) as MenuOption[];

    openMenu({
      event,
      menu: {
        title: isMultiple
          ? t("shelvesCount", { count: targetShelves.length })
          : t("shelfName", { name: shelf.getName() }),
        options,
      },
    });
  };
};
